import { useState, useEffect } from 'react';
import {
  createTicket,
  updateTicket,
  getTicketById,
  solicitarInformacoes,
  TicketCreateData,
  TRANSICOES_TICKET,
  STATUS_TICKET_LABELS,
} from '../../services/ticketService';
import { Loader2, ArrowLeft, Paperclip, Eye, Download } from 'lucide-react';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { faturarTickets } from '../../services/faturamentoService';
import { attachmentService } from '../../services/attachmentService';

interface Client {
  id: string;
  razaoSocial: string;
  nomeFantasia: string | null;
}

interface TicketFormProps {
  ticketId?: string;
  onGoBack: () => void;
  onSuccess: () => void;
}

export const TicketForm = ({ ticketId, onGoBack, onSuccess }: TicketFormProps) => {
  const isEditing = !!ticketId;

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditing);
  const [error, setError] = useState<string | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [apontamentos, setApontamentos] = useState<any[]>([]);
  const [escopoFaturado, setEscopoFaturado] = useState(false);
  const [aprovado, setAprovado] = useState(false);

  // Estado para "Solicitar Informações" (mensagem que vai para o cliente)
  const [solicitacao, setSolicitacao] = useState<{ aberta: boolean; mensagem: string }>({
    aberta: false,
    mensagem: '',
  });

  // Form data
  const [formData, setFormData] = useState<TicketCreateData>({
    clientId: '',
    titulo: '',
    descricao: '',
    prioridade: 'MEDIA',
    status: 'ABERTO',
    horasEstimadas: undefined,
    valorEstimado: undefined,
    emergencial: false,
    dataPrazo: undefined,
  });

  // Na edição, oferece apenas o status atual + transições válidas (máquina de estados)
  const currentStatus = formData.status ?? 'ABERTO';
  const statusOptions = isEditing
    ? [currentStatus, ...(TRANSICOES_TICKET[currentStatus] ?? [])]
    : Object.keys(STATUS_TICKET_LABELS);

  // Ticket emergencial não passa pela catraca de aprovação (já nasce aprovado)
  const allowedTransitions = (TRANSICOES_TICKET[currentStatus] ?? []).filter(
    (target) => !(formData.emergencial && target === 'AGUARDANDO_APROVACAO')
  );

  // Há pendência de faturamento quando existe apontamento de horas não faturado
  // ou escopo aprovado ainda não faturado.
  const temPendenciaFaturamento =
    apontamentos.some((a) => !a.faturado && !/^\[/.test(a.descricao || '')) ||
    (!!formData.valorEstimado && formData.valorEstimado > 0 && aprovado && !escopoFaturado);

  const temHorasApontadas = apontamentos.some(
    (a) => !/^\[/.test(a.descricao || '') && (a.minutosTotais || 0) > 0
  );
  const temEscopoDefinido = !!formData.valorEstimado && formData.valorEstimado > 0;

  useEffect(() => {
    loadClients();
    if (isEditing && ticketId) {
      loadTicket(ticketId);
    }
  }, [ticketId]);

  const loadClients = async () => {
    try {
      const response = await api.get('/admin/clients');
      // A API retorna { data: [...], pagination: {...} }
      setClients(response.data.data || []);
    } catch (err) {
      console.error('Erro ao carregar clientes:', err);
    }
  };

  const loadTicket = async (ticketId: string) => {
    try {
      setLoadingData(true);
      const ticket = await getTicketById(ticketId) as any;
      setApontamentos(ticket.apontamentos || []);
      setEscopoFaturado(!!ticket.escopoFaturado);
      setAprovado(!!ticket.aprovado);
      setFormData({
        clientId: ticket.clientId,
        titulo: ticket.titulo,
        descricao: ticket.descricao,
        prioridade: ticket.prioridade,
        status: ticket.status,
        horasEstimadas: ticket.horasEstimadas || undefined,
        valorEstimado: ticket.valorEstimado || undefined,
        emergencial: ticket.emergencial || false,
        dataPrazo: ticket.dataPrazo ? ticket.dataPrazo.split('T')[0] : undefined,
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao carregar ticket');
    } finally {
      setLoadingData(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validações
    if (!formData.clientId) {
      setError('Cliente é obrigatório');
      return;
    }
    if (formData.titulo.trim().length < 5) {
      setError('Título deve ter no mínimo 5 caracteres');
      return;
    }
    if (formData.descricao.trim().length < 10) {
      setError('Descrição deve ter no mínimo 10 caracteres');
      return;
    }

    try {
      setLoading(true);

      if (isEditing && ticketId) {
        // Na edição, salva apenas os campos editáveis — prioridade e status
        // são controlados à parte (prioridade do cliente; status via botões de ação).
        await updateTicket(ticketId, {
          titulo: formData.titulo,
          descricao: formData.descricao,
          horasEstimadas: formData.horasEstimadas,
          valorEstimado: formData.valorEstimado,
          dataPrazo: formData.dataPrazo || null,
        });
      } else {
        const hasEstimativa = !!formData.horasEstimadas || !!formData.valorEstimado;
        // Com estimativas, omite o status para o backend acionar a catraca (AGUARDANDO_APROVACAO)
        await createTicket({
          clientId: formData.clientId,
          titulo: formData.titulo,
          descricao: formData.descricao,
          prioridade: formData.prioridade,
          ...(hasEstimativa ? {} : { status: formData.status }),
          horasEstimadas: formData.horasEstimadas,
          valorEstimado: formData.valorEstimado,
          dataPrazo: formData.dataPrazo,
        });
      }

      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao salvar ticket');
      console.error('Erro ao salvar ticket:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusAction = async (targetStatus: 'ABERTO' | 'EM_ANDAMENTO' | 'AGUARDANDO_CLIENTE' | 'AGUARDANDO_APROVACAO' | 'RESOLVIDO' | 'FECHADO') => {
    if (!ticketId) return;

    // "Solicitar Informações" exige uma mensagem para o cliente
    if (targetStatus === 'AGUARDANDO_CLIENTE') {
      setSolicitacao({ aberta: true, mensagem: '' });
      return;
    }

    // "Enviar para Aprovação" exige prazo definido
    if (targetStatus === 'AGUARDANDO_APROVACAO' && !formData.dataPrazo) {
      setError('Informe o prazo do atendimento antes de enviar para aprovação');
      return;
    }

    // Arquivar com pendência de faturamento exige confirmação
    if (targetStatus === 'FECHADO' && temPendenciaFaturamento) {
      const confirmou = window.confirm(
        'Este ticket possui valores pendentes de faturamento. Deseja arquivar mesmo assim?'
      );
      if (!confirmou) return;
    }

    // Concluir sem horas apontadas e sem escopo definido exige confirmação
    if (targetStatus === 'RESOLVIDO' && !temHorasApontadas && !temEscopoDefinido) {
      const confirmou = window.confirm(
        'Nenhuma hora foi apontada e nenhum escopo foi definido neste ticket. Deseja concluir mesmo assim?'
      );
      if (!confirmou) return;
    }

    setLoading(true);
    setError(null);
    try {
      await updateTicket(ticketId, {
        status: targetStatus,
        horasEstimadas: formData.horasEstimadas,
        valorEstimado: formData.valorEstimado,
        dataPrazo: formData.dataPrazo || null,
      });
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao atualizar status');
      console.error('Erro ao atualizar status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEnviarSolicitacao = async () => {
    if (!ticketId) return;
    if (solicitacao.mensagem.trim().length < 10) {
      setError('A mensagem de solicitação deve ter no mínimo 10 caracteres');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await solicitarInformacoes(ticketId, solicitacao.mensagem.trim());
      setSolicitacao({ aberta: false, mensagem: '' });
      await loadTicket(ticketId);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao solicitar informações');
      console.error('Erro ao solicitar informações:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFaturar = async () => {
    if (!ticketId) return;
    setLoading(true);
    setError(null);
    try {
      await faturarTickets([ticketId]);
      await loadTicket(ticketId);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao faturar ticket');
      console.error('Erro ao faturar ticket:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onGoBack}
          className="p-2 hover:bg-muted rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-foreground" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {isEditing ? 'Editar Ticket' : 'Novo Ticket'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isEditing
              ? 'Atualize as informações do ticket'
              : 'Crie um novo ticket de atendimento'}
          </p>
        </div>
      </div>

      {/* Erro */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-400 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-card border border-border rounded-lg shadow p-6 space-y-6">
        {/* Cliente */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Cliente *
          </label>
          <select
            value={formData.clientId}
            onChange={(e) => setFormData({ ...formData, clientId: e.target.value })}
            disabled={isEditing}
            className="w-full px-4 py-2 bg-background border border-input rounded-lg text-foreground focus:ring-2 focus:ring-brand focus:border-transparent disabled:bg-muted disabled:cursor-not-allowed"
            required
          >
            <option value="">Selecione um cliente</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>
                {client.nomeFantasia || client.razaoSocial}
              </option>
            ))}
          </select>
          {isEditing && (
            <p className="text-xs text-muted-foreground mt-1">
              Cliente não pode ser alterado após criação
            </p>
          )}
        </div>

        {/* Título */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Título *
          </label>
          <input
            type="text"
            value={formData.titulo}
            onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
            placeholder="Ex: Manutenção preventiva do sistema"
            className="w-full px-4 py-2 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-brand focus:border-transparent"
            required
            minLength={5}
          />
          <p className="text-xs text-muted-foreground mt-1">Mínimo 5 caracteres</p>
        </div>

        {/* Descrição */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Descrição *
          </label>
          <textarea
            value={formData.descricao}
            onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
            placeholder="Descreva o problema ou solicitação do cliente..."
            rows={5}
            disabled={isEditing}
            className="w-full px-4 py-2 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-brand focus:border-transparent resize-none disabled:bg-muted disabled:cursor-not-allowed"
            required
            minLength={10}
          />
          <p className="text-xs text-muted-foreground mt-1">
            {isEditing
              ? 'A descrição original é somente leitura após o primeiro envio — use a timeline para interagir.'
              : 'Mínimo 10 caracteres'}
          </p>
        </div>

        {/* Prazo (deadline do atendimento) */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Prazo
          </label>
          <input
            type="date"
            value={formData.dataPrazo ?? ''}
            onChange={(e) => setFormData({ ...formData, dataPrazo: e.target.value })}
            className="w-full px-4 py-2 bg-background border border-input rounded-lg text-foreground focus:ring-2 focus:ring-brand focus:border-transparent"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Prazo previsto para o atendimento (aparece nos prazos de hoje do Zooy OS)
          </p>
        </div>

        {/* Prioridade e Status */}
        {isEditing ? (
          <div className="space-y-4 border-t border-border pt-6">
            {/* Prioridade (read-only — definida pelo cliente) */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Prioridade
              </label>
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-muted text-foreground">
                  {formData.prioridade}
                </span>
                <span className="text-xs text-muted-foreground">
                  definida pelo cliente na abertura
                </span>
              </div>
            </div>

            {/* Status atual + ações de transição (fluxo guiado) */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Status
              </label>
              <div className="flex items-center gap-2 mb-3">
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-brand/10 text-brand">
                  {STATUS_TICKET_LABELS[currentStatus] ?? currentStatus}
                </span>
                {formData.emergencial && (
                  <span className="px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                    Auto-aprovado
                  </span>
                )}
              </div>

              {/* Ações contextuais por estado (uma primária + secundárias) */}
              {currentStatus === 'ABERTO' && (
                <div className="flex flex-wrap gap-2">
                  <Button type="button" onClick={() => handleStatusAction('EM_ANDAMENTO')} disabled={loading}>
                    Iniciar Atendimento
                  </Button>
                  <Button type="button" variant="outline" onClick={() => handleStatusAction('AGUARDANDO_CLIENTE')} disabled={loading}>
                    Solicitar Informações
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => handleStatusAction('FECHADO')} disabled={loading} className="text-red-600 hover:text-red-700">
                    Arquivar
                  </Button>
                </div>
              )}

              {currentStatus === 'EM_ANDAMENTO' && (
                <div className="flex flex-wrap gap-2">
                  <Button type="button" onClick={() => handleStatusAction('RESOLVIDO')} disabled={loading}>
                    Concluir
                  </Button>
                  <Button type="button" variant="outline" onClick={() => handleStatusAction('AGUARDANDO_CLIENTE')} disabled={loading}>
                    Solicitar Informações
                  </Button>
                </div>
              )}

              {currentStatus === 'AGUARDANDO_CLIENTE' && (
                <div className="flex flex-wrap gap-2">
                  <Button type="button" onClick={() => handleStatusAction('EM_ANDAMENTO')} disabled={loading}>
                    Retomar Atendimento
                  </Button>
                  <Button type="button" variant="outline" onClick={() => handleStatusAction('RESOLVIDO')} disabled={loading}>
                    Concluir
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => handleStatusAction('FECHADO')} disabled={loading} className="text-red-600 hover:text-red-700">
                    Arquivar
                  </Button>
                </div>
              )}

              {currentStatus === 'RESOLVIDO' && (
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    onClick={handleFaturar}
                    disabled={loading || !temPendenciaFaturamento}
                    title={!temPendenciaFaturamento ? 'Nenhum apontamento ou escopo pendente para faturamento' : undefined}
                  >
                    Faturar
                  </Button>
                  <Button type="button" variant="outline" onClick={() => handleStatusAction('EM_ANDAMENTO')} disabled={loading}>
                    Reabrir
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => handleStatusAction('FECHADO')} disabled={loading} className="text-red-600 hover:text-red-700">
                    Arquivar
                  </Button>
                </div>
              )}

              {currentStatus === 'AGUARDANDO_APROVACAO' && (
                <p className="text-sm text-muted-foreground">
                  Aguardando aprovação do cliente.
                </p>
              )}

              {currentStatus === 'FECHADO' && (
                <p className="text-sm text-muted-foreground">
                  Chamado encerrado.
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Prioridade */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Prioridade *
              </label>
              <select
                value={formData.prioridade}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    prioridade: e.target.value as 'BAIXA' | 'MEDIA' | 'ALTA',
                  })
                }
                className="w-full px-4 py-2 bg-background border border-input rounded-lg text-foreground focus:ring-2 focus:ring-brand focus:border-transparent"
                required
              >
                <option value="BAIXA">Baixa</option>
                <option value="MEDIA">Média</option>
                <option value="ALTA">Alta</option>
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Status *
              </label>
              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    status: e.target.value as any,
                  })
                }
                className="w-full px-4 py-2 bg-background border border-input rounded-lg text-foreground focus:ring-2 focus:ring-brand focus:border-transparent"
                required
              >
                {statusOptions.map((status) => (
                  <option key={status} value={status}>
                    {STATUS_TICKET_LABELS[status] ?? status}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Estimativas (Catraca de Aprovação) */}
        <div className="border-t border-border pt-6">
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-foreground mb-1">
              Estimativas (Opcional - Fábrica de Software)
            </h3>
            <p className="text-xs text-muted-foreground">
              💡 Preencher esses campos fará o ticket pausar automaticamente para aprovação do cliente.
              Deixe em branco para fluxo MSP (infraestrutura ágil).
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Horas Estimadas */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Horas Estimadas
              </label>
              <input
                type="number"
                value={formData.horasEstimadas || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    horasEstimadas: e.target.value ? parseFloat(e.target.value) : undefined,
                  })
                }
                placeholder="Ex: 8.5"
                step="0.5"
                min="0"
                disabled={aprovado}
                className="w-full px-4 py-2 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-brand focus:border-transparent disabled:bg-muted disabled:cursor-not-allowed"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {aprovado ? 'Escopo já aprovado — não pode ser alterado' : 'Tempo estimado em horas para conclusão'}
              </p>
            </div>

            {/* Valor Estimado */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Valor Estimado (R$)
              </label>
              <input
                type="number"
                value={formData.valorEstimado || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    valorEstimado: e.target.value ? parseFloat(e.target.value) : undefined,
                  })
                }
                placeholder="Ex: 1500.00"
                step="0.01"
                min="0"
                disabled={aprovado}
                className="w-full px-4 py-2 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-brand focus:border-transparent disabled:bg-muted disabled:cursor-not-allowed"
              />
              <p className="text-xs text-muted-foreground mt-1">
                {aprovado ? 'Escopo já aprovado — não pode ser alterado' : 'Custo estimado do serviço'}
              </p>
            </div>
          </div>

          {/* Enviar para Aprovação — ação vinculada à estimativa */}
          {isEditing &&
            (formData.horasEstimadas || formData.valorEstimado) &&
            allowedTransitions.includes('AGUARDANDO_APROVACAO') &&
            !aprovado && (
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground mb-2">
                  Com a estimativa definida, envie para aprovação do cliente antes de executar.
                </p>
                <Button
                  type="button"
                  onClick={() => handleStatusAction('AGUARDANDO_APROVACAO')}
                  disabled={loading}
                >
                  Enviar para Aprovação
                </Button>
              </div>
            )}
        </div>

        {/* Botões */}
        <div className="flex items-center justify-end gap-4 pt-4 border-t border-border">
          <Button
            type="button"
            onClick={onGoBack}
            variant="outline"
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button type="submit" disabled={loading}>
            {loading && <Loader2 className="animate-spin" />}
            {isEditing ? 'Salvar alterações' : 'Criar Ticket'}
          </Button>
        </div>
      </form>

      {/* Timeline de interações (apenas na edição) */}
      {isEditing && (
        <div className="bg-card border border-border rounded-lg shadow p-6 space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Timeline de Interações</h3>
            <p className="text-sm text-muted-foreground">
              Histórico completo de comunicação com o cliente
            </p>
          </div>

          {apontamentos.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              Nenhuma interação registrada ainda.
            </p>
          ) : (
            <div className="space-y-4">
              {apontamentos.map((item: any) => {
                const isCliente = /^\[(RESPOSTA|APROVAÇÃO|REJEIÇÃO) DO CLIENTE\]/.test(item.descricao || '');
                const clienteSelecionado = clients.find((c) => c.id === formData.clientId);
                const clienteNome = clienteSelecionado?.nomeFantasia || clienteSelecionado?.razaoSocial || 'Cliente';
                const cleanDescription = (item.descricao || '').replace(/^\[[^\]]+\]\s*/, '');
                return (
                  <div key={item.id} className="flex gap-3">
                    <div className="w-2.5 h-2.5 bg-brand rounded-full mt-1.5 flex-shrink-0" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-medium text-foreground">
                          {isCliente ? clienteNome : item.user?.name || '—'}
                        </span>
                        <span className="text-muted-foreground">
                          {new Date(item.createdAt).toLocaleString('pt-BR')}
                        </span>
                      </div>
                      <div className="bg-muted/30 rounded-lg p-3 mt-1">
                        <p className="text-sm text-foreground whitespace-pre-wrap">{cleanDescription}</p>
                        {item.anexos && item.anexos.length > 0 && (
                          <div className="flex flex-wrap gap-2 mt-2">
                            {item.anexos.map((anexo: any) => (
                              <span key={anexo.id} className="inline-flex items-center gap-2">
                                <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-background border border-border rounded-md text-xs text-muted-foreground">
                                  <Paperclip className="w-3 h-3" />
                                  {anexo.filename}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => attachmentService.view(ticketId!, anexo.id)}
                                  title="Visualizar"
                                  className="p-0.5 hover:text-brand transition-colors"
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => attachmentService.download(ticketId!, anexo.id)}
                                  title="Baixar"
                                  className="p-0.5 hover:text-brand transition-colors"
                                >
                                  <Download className="w-3.5 h-3.5" />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Modal de Solicitação de Informações */}
      {solicitacao.aberta && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg w-full max-w-lg p-6 space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-foreground">Solicitar Informações</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Descreva o que precisa do cliente. A mensagem ficará registrada na timeline e o ticket voltará para o cliente.
              </p>
            </div>
            <textarea
              value={solicitacao.mensagem}
              onChange={(e) => setSolicitacao({ ...solicitacao, mensagem: e.target.value })}
              placeholder="Ex: Precisamos que você confirme o horário disponível para a visita técnica..."
              rows={5}
              className="w-full px-4 py-2 bg-background border border-input rounded-lg text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-brand focus:border-transparent resize-none"
            />
            <p className="text-xs text-muted-foreground -mt-2">Mínimo 10 caracteres</p>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                onClick={() => setSolicitacao({ aberta: false, mensagem: '' })}
                variant="outline"
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="button"
                onClick={handleEnviarSolicitacao}
                disabled={loading || solicitacao.mensagem.trim().length < 10}
              >
                {loading && <Loader2 className="animate-spin" />}
                Enviar Solicitação
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
